/**
 * Map multi-country creative payload to per-drop rows aligned with `orderedIds`.
 */

import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';

export interface CreativeApplyPayload {
  senderNames?: string[];
  smsTexts?: string[];
  smsTextsPerDrop?: string[][];
  senderNamesPerDrop?: string[][];
  smsTextsByCountry?: Record<string, string[]>;
  senderNamesByCountry?: Record<string, string[]>;
}

function smsTextsForRow(
  i: number,
  ck: string,
  p: CreativeApplyPayload,
  hasPerDrop: boolean,
  hasCountry: boolean,
  hasFlat: boolean,
): string[] {
  if (hasPerDrop) return p.smsTextsPerDrop![i] ?? [];
  if (hasCountry) return p.smsTextsByCountry![ck] ?? [];
  if (hasFlat) return p.smsTexts ?? [];
  return [];
}

function senderNamesForRow(
  i: number,
  ck: string,
  p: CreativeApplyPayload,
  hasPerDrop: boolean,
  hasCountry: boolean,
  hasFlat: boolean,
): string[] {
  if (hasPerDrop) return p.senderNamesPerDrop![i] ?? [];
  if (hasCountry) return p.senderNamesByCountry![ck] ?? [];
  if (hasFlat) return p.senderNames ?? [];
  return [];
}

export function expandCreativeToPerDrop(
  orderedIds: string[],
  mergedDrops: PreparedDrop[],
  payload: CreativeApplyPayload,
): { perDropSmsTexts: string[][] | null; perDropSenderNames: string[][] | null } {
  const byId = new Map(mergedDrops.map((d) => [d.id, d]));
  const hasPerDropTexts = (payload.smsTextsPerDrop?.length ?? 0) > 0;
  const hasPerDropSenders = (payload.senderNamesPerDrop?.length ?? 0) > 0;
  const hasCountryTexts = !!payload.smsTextsByCountry && Object.keys(payload.smsTextsByCountry).length > 0;
  const hasCountrySenders = !!payload.senderNamesByCountry && Object.keys(payload.senderNamesByCountry).length > 0;
  const hasFlatTexts = (payload.smsTexts?.length ?? 0) > 0;
  const hasFlatSenders = (payload.senderNames?.length ?? 0) > 0;

  const hasAny =
    hasPerDropTexts ||
    hasPerDropSenders ||
    hasCountryTexts ||
    hasCountrySenders ||
    hasFlatTexts ||
    hasFlatSenders;
  if (!hasAny) return { perDropSmsTexts: null, perDropSenderNames: null };

  const textsRows: string[][] = [];
  const senderRows: string[][] = [];
  for (let i = 0; i < orderedIds.length; i++) {
    const id = orderedIds[i]!;
    const d = byId.get(id);
    const ck = d?.countryKey ?? '';
    textsRows.push(smsTextsForRow(i, ck, payload, hasPerDropTexts, hasCountryTexts, hasFlatTexts));
    senderRows.push(senderNamesForRow(i, ck, payload, hasPerDropSenders, hasCountrySenders, hasFlatSenders));
  }

  const allTextEmpty = textsRows.every((r) => r.length === 0);
  const allSenderEmpty = senderRows.every((r) => r.length === 0);
  return {
    perDropSmsTexts: allTextEmpty ? null : textsRows,
    perDropSenderNames: allSenderEmpty ? null : senderRows,
  };
}
